import React, { FC, useEffect, useState } from 'react';

import classNames from 'classnames';

import {
	BUTTON_TYPES,
	EditIcon,
	EvButton,
	EvIcon,
	EvSection,
	EvSpinner,
	EvTitle,
	LinkIcon
} from '@evinced-private/ui-common';

import {
	WFA_SESSION_COPY_URL_BUTTON_DAID,
	WFA_SESSION_HEADER_DAID,
	WFA_SESSION_OPEN_EDIT_SESSION_MODAL_DAID,
	WFA_SESSION_TITLE_DAID
} from 'data-automation-ids/WfaDashboardAutomationIds';
import { EditSessionPopup } from 'src/apps/wfa/components/edit-session-popup/EditSessionPopup';
import { useSetWfaBreadcrumbs } from 'src/apps/wfa/hooks/useSetWfaBreadcrumbs';
import { getCurrentWFAFullUrl } from 'src/apps/wfa/services/ShareableLinksService';
import { getWfaSessionDetails } from 'src/apps/wfa/services/WfaDashboardService';
import { IWfaLabel } from 'src/apps/wfa/services/WfaDashboardServiceTypes';
import { CopyToClipboard } from 'src/common/components/copy-to-clipboard/CopyToClipboard';
import { ReportInfo } from 'src/common/components/report-info/ReportInfo';
import { useEntityStatusQuery } from 'src/common/hooks/useEntityStatusQuery';
import {
	ENTITY_STATUS_DELETED,
	ENTITY_STATUS_LOADING,
	ENTITY_STATUS_SYNCING
} from 'src/common/hooks/useEntityStatusQuery.types';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import {
	WFA_SESSION_DETAILS,
	WFA_SESSIONS
} from 'src/common/providers/reactQueryProvider/QueryKeys';
import { queryClient } from 'src/common/providers/reactQueryProvider/ReactQueryProvider';
import { Logger } from 'src/common/services/Logger';

import { getWfaInfoItems } from './WfaSingleSessionHeaderHelper';

import './WfaSingleSessionHeader.scss';

const EDIT_SESSION_BUTTON_LABEL = 'Edit Session Button';

interface IWfaSessionHeaderProps {
	sessionId: string;
	onSessionNotFoundCallback: () => void;
	onSessionDeletedCallback: () => void;
}

export const WfaSingleSessionHeader: FC<IWfaSessionHeaderProps> = ({
	sessionId,
	onSessionNotFoundCallback,
	onSessionDeletedCallback
}) => {
	const { configuration } = useConfiguration();
	const isLabelsToggleEnabled = (configuration?.WFA?.TOGGLES as Record<string, boolean>)
		?.ENABLE_PLATFORM_LABELS as boolean;

	const { query: sessionDetailsQuery, status } = useEntityStatusQuery({
		queryKey: [WFA_SESSION_DETAILS, sessionId],
		queryFn: () => getWfaSessionDetails(sessionId, isLabelsToggleEnabled)
	});
	const { name, labels } = sessionDetailsQuery.data ?? {};
	const [isEditSessionModalOpen, setIsEditSessionModalOpen] = useState(false);
	const [lastUpdatedName, setLastUpdatedName] = useState<string | null>(null);
	const [lastUpdatedLabels, setLastUpdatedLabels] = useState<IWfaLabel[] | null>(null);

	const currentName = lastUpdatedName ?? name;
	const currentLabels = lastUpdatedLabels ?? labels;

	useSetWfaBreadcrumbs(sessionId, currentName);

	useEffect(() => {
		if (!sessionId || (sessionDetailsQuery.isError && status !== ENTITY_STATUS_DELETED)) {
			onSessionNotFoundCallback();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionId, sessionDetailsQuery.isError, status]);

	useEffect(() => {
		if (status === ENTITY_STATUS_DELETED) {
			onSessionDeletedCallback();
		}
	}, [status, onSessionDeletedCallback]);

	const handleUpdate = async (newName?: string, selectedLabels?: IWfaLabel[]): Promise<void> => {
		try {
			// set the values to update the ui immediately because the update api
			// returns a 200 but actually updates in ~10 seconds so refetching immediately won't work
			if (newName) {
				setLastUpdatedName(newName);
			}
			if (selectedLabels) {
				setLastUpdatedLabels(selectedLabels);
			}
			// invalidate the queries to refetch later
			await queryClient.invalidateQueries({ queryKey: [WFA_SESSION_DETAILS, sessionId] });
			await queryClient.invalidateQueries({ queryKey: [WFA_SESSIONS] });
		} catch (e) {
			Logger.error('Error invalidating session data query after update', e);
		}
	};

	if (!sessionId || sessionDetailsQuery.isError || status === ENTITY_STATUS_DELETED) {
		return null;
	}

	return status === ENTITY_STATUS_LOADING || status === ENTITY_STATUS_SYNCING ? (
		<EvSpinner />
	) : (
		<div className="wfa-single-session-header" data-automation-id={WFA_SESSION_HEADER_DAID}>
			<div className="title-container">
				<EvTitle
					titleText={currentName}
					headingLevel={1}
					className="main-title page-heading"
					dataAutomationId={WFA_SESSION_TITLE_DAID}
				/>
				<CopyToClipboard
					renderJustIcon
					renderTooltip
					textToCopy={getCurrentWFAFullUrl(sessionId)}
					className="copy-link-button"
					iconTitle="Copy Session URL"
					icon={LinkIcon}
					dataAutomationId={WFA_SESSION_COPY_URL_BUTTON_DAID}
				/>
				<EvButton
					ariaLabel={EDIT_SESSION_BUTTON_LABEL}
					type={BUTTON_TYPES.ICON}
					onClick={() => setIsEditSessionModalOpen(true)}
					className="edit-session-button"
					dataAutomationId={WFA_SESSION_OPEN_EDIT_SESSION_MODAL_DAID}
				>
					<EvIcon className="edit-icon" icon={EditIcon} />
				</EvButton>
			</div>
			<EvSection
				ariaLabel={`Session ${currentName} info`}
				skipLinkId="session-info-section"
				ariaLive="polite"
				className={classNames('dashboard-section', 'web-sdk-test-info-section')}
			>
				<ReportInfo
					titleText="Session Info"
					reportInfo={getWfaInfoItems(currentLabels)}
					reportDetails={sessionDetailsQuery.data ?? undefined}
				/>
			</EvSection>
			{isEditSessionModalOpen && (
				<EditSessionPopup
					isOpen={isEditSessionModalOpen}
					onClose={() => setIsEditSessionModalOpen(false)}
					onSubmit={handleUpdate}
					sessionId={sessionId}
					sessionName={currentName}
					labels={currentLabels}
				/>
			)}
		</div>
	);
};
